/**
 * Product Enhancement JavaScript
 * Adds interactive features to improve product attractiveness and conversion
 */

class ProductEnhancements {
  constructor() {
    this.init();
  }

  init() {
    this.initAddToCartFeedback();
    this.initPriceUpdateAnimation();
    this.initViewersCount();
    this.initRecentPurchases();
  }

  /**
   * Add visual feedback when product is added to cart
   */
  initAddToCartFeedback() {
    document.addEventListener('click', (e) => {
      const addToCartButton = e.target.closest('.add-to-cart__button button, .quick-add__button');

      if (addToCartButton && !addToCartButton.disabled) {
        // Add success class temporarily
        setTimeout(() => {
          addToCartButton.classList.add('add-to-cart__button--success');

          // Remove after animation
          setTimeout(() => {
            addToCartButton.classList.remove('add-to-cart__button--success');
          }, 600);
        }, 100);
      }
    });
  }

  /**
   * Animate price changes when variant is selected
   */
  initPriceUpdateAnimation() {
    document.addEventListener('DOMContentLoaded', () => {
      const priceElements = document.querySelectorAll('product-price');

      priceElements.forEach(priceEl => {
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'childList' || mutation.type === 'characterData') {
              priceEl.setAttribute('data-updating', 'true');

              setTimeout(() => {
                priceEl.removeAttribute('data-updating');
              }, 400);
            }
          });
        });

        observer.observe(priceEl, {
          childList: true,
          characterData: true,
          subtree: true
        });
      });
    });
  }

  /**
   * Optional: Show "X people viewing this" indicator
   * This generates a random but consistent number based on product ID
   */
  initViewersCount() {
    const products = document.querySelectorAll('product-card[data-product-id]');

    products.forEach(product => {
      const productId = product.dataset.productId;
      if (!productId) return;

      // Generate consistent "random" number based on product ID
      const viewerCount = this.generateConsistentRandom(productId, 3, 15);

      // Only show on hover and if setting is enabled
      if (product.dataset.showViewers === 'true') {
        product.addEventListener('mouseenter', () => {
          this.showSocialProof(product, viewerCount);
        }, { once: true });
      }
    });
  }

  /**
   * Show social proof indicator
   */
  showSocialProof(productCard, count) {
    const existingProof = productCard.querySelector('.product-social-proof');
    if (existingProof) return;

    const socialProof = document.createElement('div');
    socialProof.className = 'product-social-proof';
    socialProof.innerHTML = `
      <span class="product-social-proof__icon">👁️</span>
      <span>${count} ${count === 1 ? 'person' : 'people'} viewing this</span>
    `;

    const infoContainer = productCard.querySelector('.product-info-enhancements');
    if (infoContainer) {
      infoContainer.appendChild(socialProof);
    }
  }

  /**
   * Optional: Show "Recently purchased" notifications
   */
  initRecentPurchases() {
    // This would integrate with your actual purchase data
    // For demo purposes, this is a placeholder
    if (window.shopifyProductPurchases) {
      this.showRecentPurchaseNotification();
    }
  }

  /**
   * Generate consistent pseudo-random number based on seed
   */
  generateConsistentRandom(seed, min, max) {
    // Simple hash function to generate consistent number
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      const char = seed.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }

    const normalized = Math.abs(hash) / 2147483647; // Normalize to 0-1
    return Math.floor(normalized * (max - min + 1)) + min;
  }

  /**
   * Add pulse effect to badges
   */
  static pulseBadge(badgeElement) {
    if (!badgeElement) return;

    badgeElement.style.animation = 'none';
    setTimeout(() => {
      badgeElement.style.animation = '';
    }, 10);
  }

  /**
   * Update stock indicator dynamically
   */
  static updateStockIndicator(productCard, stockLevel) {
    const stockIndicator = productCard.querySelector('.product-stock-indicator');
    if (!stockIndicator) return;

    stockIndicator.classList.remove(
      'product-stock-indicator--in-stock',
      'product-stock-indicator--low-stock',
      'product-stock-indicator--out-of-stock'
    );

    if (stockLevel === 0) {
      stockIndicator.classList.add('product-stock-indicator--out-of-stock');
      stockIndicator.textContent = 'Out of Stock';
    } else if (stockLevel <= 10) {
      stockIndicator.classList.add('product-stock-indicator--low-stock');
      stockIndicator.textContent = `Only ${stockLevel} left!`;
    } else {
      stockIndicator.classList.add('product-stock-indicator--in-stock');
      stockIndicator.textContent = 'In Stock';
    }
  }
}

// Initialize enhancements when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductEnhancements();
  });
} else {
  new ProductEnhancements();
}

// Export for potential external use
window.ProductEnhancements = ProductEnhancements;
